import { useRef } from 'react';
import { Box, Button, Dialog, Link, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

const TERMS_PDF_PATH = '/terms/digitane-ai-terms.pdf';

const RULE_KEYS = [
  'usageGuide.rules.own',
  'usageGuide.rules.mistake',
  'usageGuide.rules.privacy',
  'usageGuide.rules.misuse',
];

interface UsageGuideDialogProps {
  isOpen: boolean;
  isRequired: boolean;
  onClose: () => void;
  onAcknowledge: () => void;
}

export const UsageGuideDialog = ({
  isOpen,
  isRequired,
  onClose,
  onAcknowledge,
}: UsageGuideDialogProps) => {
  const { t } = useTranslation();
  const confirmRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => !e.open && onClose()}
      placement="center"
      closeOnInteractOutside={!isRequired}
      closeOnEscape={!isRequired}
      initialFocusEl={() => confirmRef.current}
    >
      <Dialog.Backdrop bg="blackAlpha.600" />
      <Dialog.Positioner>
        <Dialog.Content borderRadius="xl" boxShadow="xl" mx={4} maxW="lg" overflow="hidden">
          <Dialog.Header bg="gradientSecondary" px={6} py={3}>
            <Dialog.Title fontSize="lg" fontWeight="bold" color="text.primary">
              {t('usageGuide.title')}
            </Dialog.Title>
          </Dialog.Header>

          <Dialog.Body px={6} py={4}>
            <Text mb={4} fontSize="lg" fontWeight="bold" lineHeight="1.6" color="text.primary">
              {t('usageGuide.leadPrefix')}
              <Text as="span" bg="gradientPrimary" bgClip="text" color="transparent">
                {t('usageGuide.leadHighlight')}
              </Text>
              {t('usageGuide.leadSuffix')}
            </Text>

            <Stack gap={2} mb={4}>
              {RULE_KEYS.map((key) => (
                <Box key={key} display="flex" alignItems="center" gap={2}>
                  <Box w={2} h={2} flexShrink={0} borderRadius="full" bg="gradientPrimary" />
                  <Text fontSize="md" lineHeight="1.6" color="text.primary">
                    {t(key)}
                  </Text>
                </Box>
              ))}
            </Stack>

            <Box
              borderRadius="lg"
              border="1px solid"
              borderColor="borders.accent"
              bg="surfaces.accent"
              px={4}
              py={3}
            >
              <Text fontSize="sm" lineHeight="1.6" color="text.secondary">
                <Link
                  href={TERMS_PDF_PATH}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="secondary"
                  fontWeight="semibold"
                  textDecoration="underline"
                  _focus={{ outline: 'none', boxShadow: 'none' }}
                  _focusVisible={{ outline: 'none', boxShadow: 'none' }}
                >
                  {t('usageGuide.terms.linkText')}
                </Link>
                {t('usageGuide.terms.suffix')}
              </Text>
            </Box>
          </Dialog.Body>

          <Dialog.Footer justifyContent="center" px={6} pb={6}>
            <Button
              ref={confirmRef}
              variant="gradient-primary"
              size="lg"
              px={10}
              borderRadius="lg"
              _focus={{ outline: 'none', boxShadow: 'none' }}
              _focusVisible={{ outline: 'none', boxShadow: 'none' }}
              onClick={onAcknowledge}
            >
              {t('usageGuide.confirm')}
            </Button>
          </Dialog.Footer>

          {!isRequired && <Dialog.CloseTrigger />}
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
